import React from 'react';
import { Menu, MenuItem } from '@mui/material';
import { COLOR } from '@src/styles/color';

// Sizing from the design (node 2571-20899): a >=211px-wide card with 8px
// padding, 40px rows, an 8px gap after a 24px icon, and 14/20 -0.4px text.
const MENU_MIN_WIDTH = 211;

const paperSx = {
  minWidth: MENU_MIN_WIDTH,
  borderRadius: '12px',
  '& .MuiList-root': { padding: '8px' },
};

const itemSx = {
  gap: '8px',
  minHeight: 40,
  padding: '8px',
  borderRadius: '8px',
  fontSize: 14,
  lineHeight: '20px',
  letterSpacing: '-0.4px',
  color: COLOR.darkGray,
  '& svg': { fontSize: 24 },
};

/**
 * Right-click style menu shared by timeline tracks. Renders one row per entry in
 * `items` ({ key, label, icon, onClick, disabled? }); the caller keeps the open
 * state and decides what the rows do. Sized to the design, incl. a minimum width
 * so a single short row (e.g. "Xóa") still reads as a full menu card.
 *
 * Positioning: pass `anchorPosition` ({ top, left } in client coords) to open at
 * the cursor — the right-click case, which stays correct on a clip too narrow to
 * anchor to — or `anchorEl` to anchor to an element. `anchorPosition` wins.
 *
 * It's opened by mouse, so the first item isn't pre-highlighted (MUI focuses it
 * by default for keyboard nav). Rows only mount while open, so passing an empty
 * `items` when closed costs nothing.
 */
const ClipMenu = ({ anchorEl, anchorPosition, open, onClose, items }) => {
  const useCursor = Boolean(anchorPosition);
  return (
    <Menu
      open={open}
      onClose={onClose}
      anchorReference={useCursor ? 'anchorPosition' : 'anchorEl'}
      anchorEl={useCursor ? undefined : anchorEl}
      anchorPosition={anchorPosition}
      MenuListProps={{ autoFocusItem: false }}
      PaperProps={{ sx: paperSx }}
    >
      {items.map(({ key, label, icon, onClick, disabled = false }) => (
        <MenuItem key={key} sx={itemSx} onClick={onClick} disabled={disabled}>
          {icon}
          {label}
        </MenuItem>
      ))}
    </Menu>
  );
};

export default ClipMenu;
